import React, { useState, useEffect, useRef } from 'react';
import { PaperAirplaneIcon, ExclamationCircleIcon, DocumentDuplicateIcon, CheckIcon } from '@heroicons/react/24/outline';

const ChatComponent = ({ messages, onSendMessage, isLoading, error }) => {
  const [input, setInput] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Auto-focus input after responses and always keep it focused
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLoading, messages]);

  // Focus input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const message = input.trim();
    setInput('');
    
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = '24px';
    }
    
    onSendMessage(message);
    
    // Ensure focus returns to input after sending
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 100);
  };

  const copyToClipboard = async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex flex-col h-full max-w-6xl mx-auto">
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-40 space-y-6 scroll-smooth">
        {messages.length === 0 && !isLoading && (
          <div className="flex items-center justify-center min-h-[70vh] text-gray-500 dark:text-[#9aa5ce]">
            <div className="flex flex-col items-center justify-center w-full text-center space-y-6 max-w-md">
              <div className="w-64 h-64 flex items-center justify-center mb-2 rounded-3xl shadow-lg border border-gray-200 dark:border-gray-700" style={{ background: '#03101f' }}>
                <img src="/everfield-logo.png" alt="Everfield Logo" className="w-64 h-64 object-contain rounded-2xl" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-[#c0caf5]">Welcome to EverfieldGPT</h2>
              <p className="text-lg text-gray-600 dark:text-[#9aa5ce]">
                Start a conversation by typing a message below. I'm powered by GPT-5.6 Sol and ready to help!
              </p>
            </div>
          </div>
        )}
        
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in group`}
          >
            <div className={`flex flex-col ${message.role === 'user' ? 'items-end' : 'items-start'} max-w-[85%]`}>
              <div
                className={`message-bubble px-6 py-4 rounded-2xl shadow-lg
                  ${message.role === 'user'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white ml-4 shadow-blue-500/30'
                    : 'bg-white/90 dark:bg-[#24283b]/90 text-gray-800 dark:text-[#c0caf5] mr-4 backdrop-blur-xl'
                  }`}
              >
                <p className="whitespace-pre-wrap leading-relaxed font-medium select-text">
                  {message.content}
                </p>
              </div>
              
              {/* Timestamp and Copy Button Container */}
              <div className={`flex items-center gap-2 mt-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200
                             ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                <div className={`text-xs text-gray-500 dark:text-[#9aa5ce]
                               ${message.role === 'user' ? 'text-right' : 'text-left'}`}>
                  {message.timestamp ? formatTimestamp(message.timestamp) : formatTimestamp(Date.now())}
                </div>
                
                {/* Copy Button */}
                <button
                  onClick={() => copyToClipboard(message.content, `${index}-${message.role}`)}
                  className={`p-1.5 rounded-lg transition-all duration-200 hover:scale-110
                             hover:bg-gray-100 dark:hover:bg-[#414868]/50
                             text-gray-500 dark:text-[#9aa5ce] hover:text-gray-700 dark:hover:text-[#c0caf5]`}
                  title="Copy message"
                >
                  {copiedMessageId === `${index}-${message.role}` ? (
                    <CheckIcon className="h-3 w-3" />
                  ) : (
                    <DocumentDuplicateIcon className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start animate-fade-in">
            <div className="message-bubble relative max-w-[85%] px-6 py-4 rounded-2xl bg-white/90 dark:bg-[#24283b]/90 backdrop-blur-xl shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="flex space-x-1">
                  <div className="w-2.5 h-2.5 bg-[#7aa2f7] rounded-full animate-bounce opacity-75" />
                  <div className="w-2.5 h-2.5 bg-[#7aa2f7] rounded-full animate-bounce opacity-75" style={{ animationDelay: '0.2s' }} />
                  <div className="w-2.5 h-2.5 bg-[#7aa2f7] rounded-full animate-bounce opacity-75" style={{ animationDelay: '0.4s' }} />
                </div>
                <span className="text-sm text-gray-500 dark:text-[#9aa5ce] font-medium">
                  EverfieldGPT is thinking...
                </span>
              </div>
            </div>
          </div>
        )}
        
        {error && (
          <div className="flex justify-center animate-fade-in">
            <div className="flex items-center space-x-2 px-6 py-4 rounded-xl bg-red-50/90 dark:bg-[#f7768e]/20
                          text-red-600 dark:text-[#f7768e] shadow-lg border border-red-100 dark:border-[#f7768e]/50
                          backdrop-blur-xl">
              <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
              <div className="flex flex-col">
                <p className="text-sm font-medium">{error}</p>
                <p className="text-xs opacity-75 mt-1">
                  You can try again with a shorter message or wait a moment.
                </p>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Floating Sticky Input Container */}
      <div className="fixed bottom-0 left-0 right-0 p-4">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSend} className="relative">
            {/* Ultra Modern Input Container with Thick Colored Border */}
            <div
              className={`relative rounded-3xl transition-all duration-300 ease-out transform cursor-text
                         ${isFocused
                           ? 'scale-[1.02] shadow-2xl shadow-blue-500/25'
                           : 'scale-100 shadow-xl'
                         }`}
              onClick={() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }}
            >
              
              {/* Animated Gradient Border */}
              <div className={`absolute inset-0 rounded-3xl transition-all duration-500 ease-out
                             ${isFocused
                               ? 'bg-gradient-to-r from-[#7aa2f7] via-[#bb9af7] to-[#7dcfff] p-[2px]'
                               : 'bg-gradient-to-r from-gray-200 to-gray-300 dark:from-[#414868] dark:to-[#565f89] p-[1px]'
                             }`}>
                <div className="h-full w-full rounded-3xl bg-white dark:bg-[#1a1b26]"></div>
              </div>
              
              {/* Inner Content Container */}
              <div className="relative flex items-center gap-4 p-6 rounded-3xl bg-white/95 dark:bg-[#1a1b26]/95 backdrop-blur-xl">
                <div className="flex-1 flex items-center">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend(e);
                      }
                    }}
                    placeholder="Type your message..."
                    className="w-full bg-transparent border-none outline-none resize-none
                             text-gray-900 dark:text-[#c0caf5] placeholder-gray-400 dark:placeholder-[#9aa5ce]
                             text-lg leading-7 max-h-40 min-h-[28px] font-medium
                             scrollbar-hide flex items-center"
                    disabled={isLoading}
                    rows={1}
                    style={{
                      height: 'auto',
                      minHeight: '28px',
                      maxHeight: '160px',
                      lineHeight: '1.75rem',
                      paddingTop: '2px',
                      paddingBottom: '2px'
                    }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                    }}
                  />
                </div>
                
                {/* Ultra Modern Send Button */}
                <button
                  type="submit"
                  disabled={isLoading || !input.trim()}
                  className={`flex-shrink-0 p-4 rounded-2xl text-white font-semibold
                           transition-all duration-300 ease-out transform
                           ${isLoading || !input.trim()
                             ? 'bg-gray-400 dark:bg-[#565f89] cursor-not-allowed scale-95 opacity-50'
                             : isFocused
                               ? 'bg-gradient-to-r from-[#7aa2f7] via-[#bb9af7] to-[#7dcfff] hover:from-[#6d8ff7] hover:via-[#a78af7] hover:to-[#73d0ff] scale-110 shadow-lg shadow-[#7aa2f7]/30'
                               : 'bg-gradient-to-r from-[#7aa2f7] to-[#7dcfff] hover:from-[#6d8ff7] hover:to-[#73d0ff] hover:scale-105 shadow-lg'
                           } group`}
                  title="Send message"
                >
                  <PaperAirplaneIcon className={`h-6 w-6 transform transition-all duration-200
                                                ${!isLoading && input.trim() ? 'group-hover:translate-x-1 group-hover:-translate-y-0.5' : ''}`} />
                </button>
              </div>
              
              {/* Glowing Effect when Focused */}
              <div className={`absolute inset-0 rounded-3xl transition-all duration-500 ease-out -z-10 blur-xl
                             ${isFocused
                               ? 'bg-gradient-to-r from-[#7aa2f7]/30 via-[#bb9af7]/30 to-[#7dcfff]/30 opacity-100 scale-105'
                               : 'bg-gradient-to-r from-[#7aa2f7]/10 via-[#bb9af7]/10 to-[#7dcfff]/10 opacity-0 scale-100'
                             }`}></div>
            </div>
            
            {/* Enhanced Input Hint */}
            <div className={`flex justify-between items-center mt-3 px-4 text-sm transition-all duration-200
                           ${isFocused
                             ? 'text-blue-600 dark:text-[#7aa2f7] opacity-100'
                             : 'text-gray-500 dark:text-[#9aa5ce] opacity-75'
                           }`}>
              <span className="flex items-center gap-2">
                <span className="hidden sm:inline">Press Enter to send, Shift+Enter for new line</span>
                <span className="sm:hidden">Enter to send</span>
              </span>
              <span className={`font-mono text-xs px-2 py-1 rounded-lg transition-colors duration-200
                             ${input.length > 1000
                               ? 'bg-orange-100 dark:bg-[#e0af68]/20 text-orange-600 dark:text-[#e0af68]'
                               : isFocused
                                 ? 'bg-blue-50 dark:bg-[#7aa2f7]/20 text-blue-600 dark:text-[#7aa2f7]'
                                 : 'bg-gray-100 dark:bg-[#414868] text-gray-600 dark:text-[#9aa5ce]'
                             }`}>
                {input.length.toLocaleString()} chars
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChatComponent;
